/**
 * Author: Andrey Morozov
 * Confidential. Proprietary code. Do not distribute without authorization.
 *
 * CallVoiceClient.ts — Cal\1 \2oic\1 \2lient (services/operator-web-app).
 */

/**
 * E3-S1: WebSocket client for /ws/call — call_start handshake, binary PCM16 send/receive.
 * E3-S2: Implements ICallVoiceTransport for transport abstraction.
 * Compatible with sip-gateway handleWebSocketCall. Sends 100ms chunks (1600 samples).
 */
import type { WsCallStartMessage, WsCallOutMessage } from './ws-call-types';
import { PlaybackQueue } from './PlaybackQueue';
import type {
  CallVoiceState,
  CallVoiceConnectParams,
  CallVoiceTransportCallbacks,
  ICallVoiceTransport,
} from './types';

const SEND_CHUNK_MS = 100;
const SAMPLE_RATE = 8000;
const SEND_CHUNK_SAMPLES = (SAMPLE_RATE * SEND_CHUNK_MS) / 1000; // 800

export type { CallVoiceState, CallVoiceTransportCallbacks };
export type CallVoiceClientCallbacks = CallVoiceTransportCallbacks;

export class CallVoiceClient implements ICallVoiceTransport {
  private url: string;
  private ws: WebSocket | null = null;
  private callbacks: CallVoiceClientCallbacks;
  private playback = new PlaybackQueue();
  private state: CallVoiceState = 'disconnected';
  private preferredOutputId: string | null = null;

  constructor(url: string, callbacks: CallVoiceClientCallbacks = {}) {
    this.url = url;
    this.callbacks = callbacks;
  }

  connect(params: CallVoiceConnectParams): void {
    const { calleeId, callId, sessionId, preferredOutputId } = params;
    this.preferredOutputId = preferredOutputId ?? null;
    if (this.ws?.readyState === WebSocket.OPEN || this.ws?.readyState === WebSocket.CONNECTING) {
      this.callbacks.onError?.('Already connected or connecting');
      return;
    }
    this.setState('connecting');
    try {
      this.ws = new WebSocket(this.url);
    } catch {
      this.setState('disconnected');
      this.callbacks.onError?.('WebSocket failed');
      return;
    }
    this.ws.binaryType = 'arraybuffer';
    this.ws.onopen = () => {
      this.setState('starting');
      const msg: WsCallStartMessage = {
        type: 'call_start',
        callee_id: calleeId,
        ...(callId && { call_id: callId }),
        ...(sessionId && { session_id: sessionId }),
      };
      this.ws!.send(JSON.stringify(msg));
    };
    this.ws.onclose = () => {
      this.ws = null;
      this.playback.stop();
      this.setState('ended');
    };
    this.ws.onerror = () => {
      this.callbacks.onError?.('WebSocket error');
    };
    this.ws.onmessage = (ev: MessageEvent) => {
      if (ev.data instanceof ArrayBuffer) {
        this.playback.push(ev.data);
        return;
      }
      try {
        const msg = JSON.parse(ev.data as string) as WsCallOutMessage;
        if (msg.type === 'session_started') {
          this.playback.start({ outputDeviceId: this.preferredOutputId }).catch(() => {});
          this.setState('active');
          this.callbacks.onSessionStarted?.(msg.call_id, msg.session_id);
        } else if (msg.type === 'error') {
          this.callbacks.onError?.(msg.data ?? 'Server error');
        }
      } catch {
        // ignore non-JSON
      }
    };
  }

  sendPcm16(pcm16: Int16Array | ArrayBuffer): void {
    if (this.ws?.readyState !== WebSocket.OPEN) return;
    const buf = pcm16 instanceof ArrayBuffer ? pcm16 : pcm16.buffer;
    this.ws.send(buf);
  }

  /** Send 100ms chunks from a stream; call from mic pipeline. */
  sendChunk(pcm16Chunk: Int16Array): void {
    if (pcm16Chunk.length !== SEND_CHUNK_SAMPLES) {
      this.ws?.send(pcm16Chunk.buffer);
      return;
    }
    this.ws?.send(pcm16Chunk.buffer);
  }

  endCall(): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify({ type: 'call_end' }));
    }
    this.ws?.close();
    this.ws = null;
    this.playback.stop();
    this.setState('ended');
  }

  getState(): CallVoiceState {
    return this.state;
  }

  private setState(s: CallVoiceState): void {
    this.state = s;
    this.callbacks.onState?.(s);
  }
}
